import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Download,
  FileText,
  Search,
} from "lucide-react";
import ReactCountryFlag from "react-country-flag";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils/cn";
import { usePageTitle } from "@/hooks/usePageTitle";
import { withBranding, formatDateTimeDmy } from "@/utils/helper";
import {
  useListPayrollBranchesQuery,
  useListPaymentFilesQuery,
  type IPaymentFileListItem,
  type IPaymentFileMonthGroup,
} from "@/services/(company)/payrollRunsApi";
import { useDownloadPaymentFile } from "@/services/(company)/paymentFileDownload";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

const buildStatusOptions = (t: TFunction) => [
  { value: "all", label: t("company:payrolls.all_statuses") },
  { value: "READY", label: t("company:payrolls.ready") },
  { value: "REPLACED", label: t("company:payrolls.replaced") },
];

const buildStatusBadge = (
  t: TFunction,
): Record<string, { label: string; className: string }> => ({
  READY: {
    label: t("company:payrolls.ready"),
    className: "bg-success/10 text-success",
  },
  REPLACED: {
    label: t("company:payrolls.replaced"),
    className: "bg-gray-100 text-gray-600",
  },
});

const formatAmount = (v: number | string, currency?: string) => {
  const n = typeof v === "string" ? Number(v.replace(/,/g, "")) : v;
  if (!Number.isFinite(n)) return "—";
  const amount = n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${currency} ${amount}` : amount;
};

const safeDate = (iso: string | null | undefined) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : formatDateTimeDmy(d);
};

const FilterSelect = ({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
}) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9 w-36 bg-white text-xs font-medium">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => (
        <SelectItem key={o.value} value={o.value}>
          {o.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const FileRow = ({
  file,
  onDownload,
  isDownloading,
}: {
  file: IPaymentFileListItem;
  onDownload: () => void;
  isDownloading: boolean;
}) => {
  const { t } = useTranslation(["company", "common"]);
  const badge = buildStatusBadge(t)[file.status] ?? {
    label: file.status,
    className: "bg-gray-100 text-gray-600",
  };
  const generated = safeDate(file.generatedAt);
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-border first:border-t-0">
      <div className="flex items-center gap-3 min-w-0">
        <div className="size-9 shrink-0 rounded-xs flex items-center justify-center bg-gray-50">
          {file.country?.code ? (
            <ReactCountryFlag
              countryCode={file.country.code}
              svg
              style={{ width: "1.6em", height: "1.2em", borderRadius: "1px" }}
            />
          ) : (
            <FileText size={18} className="text-primary" />
          )}
        </div>
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-semibold text-gray-900 truncate">
            {file.displayName || file.title}
          </span>
          <span className="text-[11px] text-gray-400 truncate">
            {[
              file.title,
              file.runCode,
              file.bank?.name,
              file.employeeCount > 0 ? `${file.employeeCount} emp` : null,
              file.fileType !== "STANDARD" ? file.fileType : null,
              generated,
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-4 shrink-0">
        <span className="text-sm font-medium text-gray-900">
          {formatAmount(file.totalAmount, file.currency)}
        </span>
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-xs px-2.5 py-1 text-xs font-semibold",
            badge.className,
          )}
        >
          <span className="size-1.5 rounded-full bg-current" />
          {badge.label}
        </span>
        <Button
          variant="outline-success"
          size="icon-sm"
          disabled={isDownloading}
          data-tooltip-id="hrm-tooltip"
          data-tooltip-content={t("company:payrolls.download_payment_file")}
          onClick={onDownload}
        >
          <Download size={14} />
        </Button>
      </div>
    </div>
  );
};

const MonthAccordion = ({
  group,
  expanded,
  onToggle,
  onDownload,
  isDownloading,
}: {
  group: IPaymentFileMonthGroup;
  expanded: boolean;
  onToggle: () => void;
  onDownload: (file: IPaymentFileListItem) => void;
  isDownloading: boolean;
}) => {
  const { t } = useTranslation(["company", "common"]);
  return (
    <div className="rounded-xs border border-border bg-white overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2 w-full px-4 py-3 bg-primary/10/30 text-start"
      >
        {expanded ? (
          <ChevronDown size={16} className="text-primary" />
        ) : (
          <ChevronRight size={16} className="text-primary" />
        )}
        <span className="text-sm font-semibold text-gray-900">
          {group.label}
        </span>
        <span className="text-xs text-gray-400">
          {t("company:payrolls.run_count", { count: group.runCount })},{" "}
          {t("company:payrolls.file_count", { count: group.fileCount })}
        </span>
      </button>
      {expanded && (
        <div className="flex flex-col">
          {group.files.length === 0 ? (
            <div className="px-4 py-3 text-xs text-gray-400">
              {t("company:payrolls.no_files")}
            </div>
          ) : (
            group.files.map((f) => (
              <FileRow
                key={f.id}
                file={f}
                onDownload={() => onDownload(f)}
                isDownloading={isDownloading}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export const CompanyPaymentFiles = () => {
  const { t } = useTranslation(["company", "common"]);
  usePageTitle(withBranding(t("company:payrolls.payment_files")));

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [branch, setBranch] = useState("all");
  const [status, setStatus] = useState("all");
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const autoExpandedRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: branchesResponse } = useListPayrollBranchesQuery();
  const branches = branchesResponse?.data ?? [];

  const branchOptions = [
    { value: "all", label: t("common:ui_kit.all_branches") },
    ...branches.map((b) => ({ value: String(b.id), label: b.name })),
  ];

  const { data: response, isFetching } = useListPaymentFilesQuery({
    ...(search ? { search } : {}),
    ...(branch !== "all" ? { branchId: Number(branch) } : {}),
    ...(status !== "all" ? { status } : {}),
  });

  const groups = useMemo(() => response?.data ?? [], [response]);

  useEffect(() => {
    if (autoExpandedRef.current) return;
    if (groups.length > 0) {
      autoExpandedRef.current = true;
      setExpandedKeys(new Set([groups[0].month]));
    }
  }, [groups]);

  const { download, isDownloading } = useDownloadPaymentFile();

  const toggle = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="rounded-xs border border-border bg-white flex flex-col gap-4 p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 border border-input rounded-xs h-9 px-3 w-80 bg-white">
          <Search size={14} className="text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t(
              "company:payrolls.search_country_title_status_file_name",
            )}
            className="flex-1 bg-transparent text-xs placeholder:text-gray-400 focus:outline-hidden"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <FilterSelect
            value={branch}
            onChange={setBranch}
            options={branchOptions}
            placeholder={t("common:ui_kit.branch")}
          />
          <FilterSelect
            value={status}
            onChange={setStatus}
            options={buildStatusOptions(t)}
            placeholder={t("company:payrolls.status")}
          />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {isFetching && groups.length === 0 ? (
          <div className="text-sm text-gray-400 py-8 text-center">
            {t("company:payrolls.loading_payment_files")}
          </div>
        ) : groups.length === 0 ? (
          <div className="text-sm text-gray-400 py-8 text-center">
            {t("company:payrolls.no_payment_files_yet")}
          </div>
        ) : (
          groups.map((m) => (
            <MonthAccordion
              key={m.month}
              group={m}
              expanded={expandedKeys.has(m.month)}
              onToggle={() => toggle(m.month)}
              onDownload={(file) =>
                download(file.runId, file.id, {
                  // The bank's name, not the on-screen label — and only a
                  // fallback for when Content-Disposition is unreadable.
                  fileName: file.fileName || file.displayName,
                })
              }
              isDownloading={isDownloading}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default CompanyPaymentFiles;
